import React, { useState } from 'react';
import fr from 'facet-rest';

import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { getSectionCardStyle } from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/styles';
import useLink from 'Hooks/useLink';
import { Table, useSelectionState } from '@facet-wealth/design';
import { Button } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import { FileViewer }
  from 'Components/ComponentLibrary/Components/Uncategorized/FileViewer';
import TableBody from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Table/TableBody';
import DownloadIcon from '@mui/icons-material/esm/Download';
import { downloadSecureFileFromLink } from 'Helpers/File';
import MetaQueryFieldGroup
  from 'Views/Page/Content/Templates/ManagedAccountPageTemplate/Content/ManagedAccountPageTabPanel/Layouts/BalanceAndAllocationTabPanelLayout/ManagedAccountActivitySummary/MetaQueryFieldGroup';
import { DATE_RANGE_CUSTOM } from 'Consts/dateRange';
import useMobileLayout from 'Hooks/useMobileLayout';
import { useSnackbarContext } from 'Context/SnackbarContext';
import PropTypes from 'prop-types';

import {
  HouseholdBalanceSheetFilterLayout,
} from 'Views/Page/Content/Templates/HouseholdBalanceSheetPageTemplate/filters/HouseholdBalanceSheetFilterLayout';
import CollectionQueryForm from 'Components/ComponentLibrary/Molecules/CollectionQueryForm';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import {
  CheckboxMultiPicklist,
} from 'Views/Page/Content/Templates/HouseholdBalanceSheetPageTemplate/filters/CheckboxMultiPicklist';
import DocumentsTableRow from './DocumentsTableRow';
import DocumentsTablePagination from './DocumentsTablePagination';

const useStyles = buildUseStyles('ManagedAccountDocuments', (theme) => ({
  root: {
    ...getSectionCardStyle({ theme }),
  },
  checkboxLabel: {
    margin: 0,
  },
  checkbox: {
    padding: 0,
  },
  checkboxCell: {
    border: 'none',
    borderRadius: '16px 0 0 16px',
  },
  checkboxHeaderCell: {
    backgroundColor: theme.palette.facet.lightGray,
  },
  tableControls: {
    display: 'flex',
    flexDirection: ({ isMobile }) => (isMobile ? 'column' : 'row'),
    marginBottom: theme.spacing(3),
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  selectedGroupControls: {
    display: 'flex',
    gap: theme.spacing(1),
    marginLeft: 'auto',
  },
  dataContainer: {
    overflowX: 'auto',
  },
}));

const metaQueryOverride = (showAccountOptions) => ({ resource, metaQueryValues }) => fr.metaOverride().name('clearingHouseAccountDocumentMetaQueryOverrideSet')
  .overrideAll({
    hidden: true,
  }, { ordinal: 200 })
  .nameOverride('clearingHouseAccountId', {
    hidden: !showAccountOptions,
    default: resource.link('self')?.param('clearingHouseAccountId'),
  }, { ordinal: 201 })
  .nameOverride('documentType', {
    hidden: false,
    default: resource.link('self')?.param('documentType'),
  }, { ordinal: 201 })
  .nameOverride('datePeriod', {
    hidden: false,
    default: resource.link('self')?.param('datePeriod') || DATE_RANGE_CUSTOM,
    required: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
  }, { ordinal: 201 })
  .nameOverride('startDate', {
    hidden: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
    default: resource.link('self')?.param('startDate'),
    required: metaQueryValues.datePeriod === DATE_RANGE_CUSTOM,
    exclude: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
  }, { ordinal: 201 })
  .nameOverride('endDate', {
    hidden: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
    default: resource.link('self')?.param('endDate'),
    required: metaQueryValues.datePeriod === DATE_RANGE_CUSTOM,
    exclude: metaQueryValues.datePeriod !== DATE_RANGE_CUSTOM,
  }, { ordinal: 201 });

const filterModalMetaOverrideSet = buildFacetDesignOverride()
  .overrideAll({ hidden: true }, overrideReadOnly)
  .typeOverride('multipicklist', { _component: () => CheckboxMultiPicklist }, overrideReadOnly)
  .nameOverride('clearingHouseAccountId', { hidden: false }, overrideReadOnly)
  .nameOverride('documentType', { hidden: false }, overrideReadOnly)
  .nameOverride('datePeriod', { hidden: false }, overrideReadOnly);

export default function ManagedAccountDocuments({ baseLink, showAccountOptions }) {
  const isMobile = useMobileLayout();
  const { displayErrorMessage } = useSnackbarContext();
  const [open, setOpen] = useState(false);

  const classes = useStyles({ isMobile });

  const [activeDocument, setActiveDocument] = useState(null);
  function clearActiveDocument() {
    setActiveDocument(null);
  }

  const [link, setLink] = useState(baseLink);
  const {
    isInitialized,
    loading,
    loadingError,
    collection,
  } = useLink(link, { onError: (e) => displayErrorMessage(e, (e.errors && e.errors[0]?.detail) || 'There was a problem loading the documents.') });

  const fields = collection?.meta().template().list().filter(field => !field.attr('hidden'));
  const documents = collection?.resources().list() || [];
  const noData = loadingError || documents.length === 0;

  const {
    selectedKeys,
    isSelectedAny,
    isSelectedAll,
    isSelected,
    toggleSelect,
    toggleSelectAll,
    clearSelection,
  } = useSelectionState(documents.map(d => d.id()));

  const [downloading, setDownloading] = useState(false);

  const loadingIndicator = loading || downloading;


  function loadLink(l) {
    setLink(l);
    clearSelection();
  }

  async function loadNextData() {
    await loadLink(collection.link('next'));
  }

  async function loadPreviousData() {
    await loadLink(collection.link('prev'));
  }

  if (!isInitialized && loadingError) {
    return <Box><Subtitle value="Could not load the information. Please retry later." /></Box>;
  }

  if (!isInitialized) {
    return <LoadingIndicator />;
  }

  async function downloadSelected() {
    setDownloading(true);

    try {
      const keys = new Set(selectedKeys);
      const selectedDocuments = documents.filter(document => keys.has(document.id()));

      for (const document of selectedDocuments) {
        // eslint-disable-next-line no-await-in-loop
        await downloadSecureFileFromLink(document.relationship('download').link('related'), document.attr('name'));
      }
    } catch (e) {
      displayErrorMessage(e, (e.errors && e.errors[0]?.detail) || 'There was a problem downloading the documents.');
    }

    setDownloading(false);
  }

  return (
    <Box className={classes.root} data-test={'managed-account-documents'}>
      {activeDocument && (
        <FileViewer
          eventCategory={'Managed Account Documents'}
          attachmentResources={[activeDocument]}
          isOpen
          closeModal={clearActiveDocument}
        />)
      }

      <Box className={classes.tableControls}>

        {showAccountOptions ? (
          <CollectionQueryForm
            removeEmptyQueryParams
            component="div"
            collection={collection}
            onSubmit={(newCollection) => {
              setOpen(false);
              loadLink(newCollection.link('self'));
            }}
            LayoutProps={{
              open,
              setOpen,
              hidePrompt: true,
              additionalFields: [{
                name: 'documentType',
                resetTo: 'all',
              }, {
                name: 'datePeriod',
                resetTo: 'max',
              }],
            }}
            layout={HouseholdBalanceSheetFilterLayout}
            metaOverrideSet={filterModalMetaOverrideSet}
            formPadding="none"
            formMargin="none"
          />
        ) : (
          <MetaQueryFieldGroup
            test="document-filters"
            resource={collection}
            metaQueryName={fr.queries.getClearingHouseAccountDocuments.name}
            metaQueryOverride={metaQueryOverride(showAccountOptions)}
            onNewLink={loadLink}
          />)}

        {!loadingError && (
          <Box className={classes.selectedGroupControls}>
            <Button
              color={'primary'}
              endIcon={<DownloadIcon />}
              disabled={selectedKeys.length === 0 || (loadingIndicator)}
              onClick={downloadSelected}
            >
            Download {selectedKeys.length > 0 && selectedKeys.length}
            </Button>
            <DocumentsTablePagination
              loading={loadingIndicator}
              onNext={collection.link('next') ? loadNextData : undefined}
              onPrevious={collection.link('prev') ? loadPreviousData : undefined}
            />
          </Box>
        )}
      </Box>

      <Box className={classes.dataContainer} data-test={'documents-table'}>
        {noData && <Box><Subtitle value="No data found." /></Box>}
        {!noData && (
          <Table.Table>
            <DocumentsTableRow
              fields={fields}
              checked={isSelectedAll}
              indeterminate={isSelectedAny && !isSelectedAll}
              onCheckedChange={toggleSelectAll}
            />

            <TableBody>
              {documents.map(resource => (
                <React.Fragment key={resource.id()}>
                  <DocumentsTableRow
                    resource={resource}
                    fields={fields}
                    checked={isSelected(resource.id())}
                    onClick={() => setActiveDocument(resource)}
                    onCheckedChange={() => toggleSelect(resource.id())}
                  />
                </React.Fragment>
              ))
          }
            </TableBody>
          </Table.Table>
        )}
      </Box>
    </Box>
  );
}

ManagedAccountDocuments.defaultProps = {
  showAccountOptions: false,
};

ManagedAccountDocuments.propTypes = {
  baseLink: PropTypes.object.isRequired,
  showAccountOptions: PropTypes.bool,
};
